import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import '../AdminServiceEditor/AdminServiceEditor.css';

const AdminVolunteerEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        role: 'Volunteer',
        bio: '',
        photo_url: '',
        status: 'pending'
    });
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isNew) {
            fetchVolunteer();
        }
    }, [id, isNew]);

    const fetchVolunteer = async () => {
        try {
            const res = await fetch(`/api/volunteers/${id}`);
            if (!res.ok) throw new Error('Failed to fetch');
            const data = await res.json();
            setFormData(data);
        } catch (error) {
            alert('Error loading volunteer');
            navigate('/admin/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const url = isNew ? '/api/volunteers' : `/api/volunteers/${id}`;
            const method = isNew ? 'POST' : 'PUT';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                navigate('/admin/dashboard');
            } else {
                const error = await res.json();
                alert(error.message || 'Error saving volunteer');
            }
        } catch (error) {
            alert('Error saving volunteer');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this volunteer/member?')) return;

        try {
            const res = await fetch(`/api/volunteers/${id}`, { method: 'DELETE' });
            if (res.ok) {
                navigate('/admin/dashboard');
            } else {
                alert('Error deleting volunteer');
            }
        } catch (error) {
            alert('Error deleting volunteer');
        }
    };

    if (loading) {
        return <div className="admin-loading">Loading...</div>;
    }

    return (
        <div className="admin-editor">
            <header className="editor-header">
                <Link to="/admin/dashboard" className="back-link">← Back to Dashboard</Link>
                <h1>{isNew ? 'Add New Volunteer/Member' : 'Edit Volunteer/Member'}</h1>
            </header>

            <form onSubmit={handleSubmit} className="editor-form">
                <div className="form-group">
                    <label>Full Name *</label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter full name"
                        required
                    />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Email *</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="email@example.com"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Phone</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone || ''}
                            onChange={handleChange}
                            placeholder="0400 000 000"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Role</label>
                        <select name="role" value={formData.role} onChange={handleChange}>
                            <option value="Volunteer">Volunteer</option>
                            <option value="President">President</option>
                            <option value="Vice President">Vice President</option>
                            <option value="Secretary">Secretary</option>
                            <option value="Treasurer">Treasurer</option>
                            <option value="Committee Member">Committee Member</option>
                            <option value="Youth Coordinator">Youth Coordinator</option>
                            <option value="Women's Coordinator">Women's Coordinator</option>
                            <option value="Education Coordinator">Education Coordinator</option>
                            <option value="Events Coordinator">Events Coordinator</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Status</label>
                        <select name="status" value={formData.status} onChange={handleChange}>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label>Photo URL</label>
                    <input
                        type="text"
                        name="photo_url"
                        value={formData.photo_url || ''}
                        onChange={handleChange}
                        placeholder="/images/team/member.jpg"
                    />
                </div>

                <div className="form-group">
                    <label>Bio</label>
                    <textarea
                        name="bio"
                        value={formData.bio || ''}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Brief description about this person..."
                    />
                </div>

                <div className="form-actions">
                    <button type="submit" disabled={saving} className="save-btn">
                        {saving ? 'Saving...' : (isNew ? 'Add Member' : 'Save Changes')}
                    </button>
                    <Link to="/admin/dashboard" className="cancel-btn">Cancel</Link>
                    {!isNew && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="cancel-btn"
                            style={{ background: '#dc3545', marginLeft: 'auto' }}
                        >
                            Delete
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default AdminVolunteerEditor;
